import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  ArrowRight,
  Users,
  Activity,
  TrendingUp,
  ChevronRight,
  ChevronDown,
  Star,
  Shield,
  Zap,
  Target,
  Eye,
  GraduationCap,
  DollarSign,
  Clock,
  ChevronLeft,
  ExternalLink,
  Mail,
  Phone,
  Menu,
  X,
  Check,
  BarChart3,
  Video,
  Trophy,
  Smartphone,
  HelpCircle,
  Quote,
  MapPin,
  Clipboard,
  Radio,
  Globe,
  Timer,
  Crosshair,
  Send,
  Layers,
  Newspaper,
  Calendar,
  BadgeCheck,
  Tv,
  Crown,
  Gift,
  ShieldCheck,
  Play,
  UserPlus,
  FileText,
} from "lucide-react";
import { NSBLogo } from "../components/NSBLogo";
import { TimelineBoard } from "../components/TimelineBoard";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EXT_BASE = "https://scoutbureauos.polsia.app";

export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [topMetrics, setTopMetrics] = useState({ sixty_yard: [], exit_velocity: [] });
  const [openFaq, setOpenFaq] = useState(null);
  const [scoutWireItems, setScoutWireItems] = useState([
    "Xander Henry (2026 RHP) touched 86.8 MPH with elite vertical carry...",
    "Xan Harwell (2026 RHP) records elite 3,058 RPM spin rate on Sweeper...",
    "NSB Combine Series: Next event features FlightScope verified metrics...",
  ]);
  const tickerRef = useRef(null);
  const [newsFeed, setNewsFeed] = useState({});
  const [activeNewsDivision, setActiveNewsDivision] = useState("Inside NSB");
  const newsDivisions = ["Inside NSB", "High School", "BC9 HBCU", "D1", "D2", "D3", "NAIA", "JUCO"];

  useEffect(() => {
    fetch(`${API_URL}/api/homepage/top-metrics`).then(r => r.json()).then(d => setTopMetrics(d)).catch(() => {});
    fetch(`${API_URL}/api/scout-wire/items`).then(r => r.json()).then(d => {
      if (d.items?.length > 0) setScoutWireItems(d.items.map(i => i.text));
    }).catch(() => {});
    fetch(`${API_URL}/api/news/feed`).then(r => r.json()).then(d => {
      if (d.feed) setNewsFeed(d.feed);
    }).catch(() => {});
  }, []);

  const navTabs = [
    { label: "Home", href: "#" },
    { label: "Evaluations", href: `${EXT_BASE}/combine`, external: true },
    { label: "VPP", href: `${EXT_BASE}/vpp`, external: true },
    { label: "Combines", href: `${EXT_BASE}/combines`, external: true },
    { label: "GameDays", href: `${EXT_BASE}/gamedays`, external: true },
    { label: "Hit-a-Thon", href: `${EXT_BASE}/hitathon`, external: true },
    { label: "Leaderboard", href: "/leaderboard", internal: true },
    { label: "ScoutsEye App", href: "https://scoutseyeapp.com", external: true },
  ];

  const faqs = [
    { q: "How much does an NSB evaluation cost?", a: "Individual evaluations start at $99 for a Data Report, $200 for Data + Film, and $500 for a full Verified Player Profile (VPP). NSB Universe members save 20% on all tiers." },
    { q: "What does the evaluation process look like?", a: "You attend an NSB Combine or scheduled evaluation session. Our former MLB scouts run you through FlightScope-verified drills to capture your metrics. Within 48\u201372 hours, your verified data, video, and report are ready." },
    { q: "What is NSB Connect?", a: "NSB Connect is our free tier. You get a profile visible to college coaches, 100 Tuition Rewards points per month (1,200 per year), and one 20-second highlight video. It's limited to the first 5,000 athletes." },
    { q: "What is the VPP program?", a: "The Verified Player Profile (VPP) is our premium evaluation tier at $500. You get a full 20-80 scout report, college coach outreach, recruiting strategy call, NSB Verified Badge, and all Data + Film features." },
    { q: "What is GameDay+?", a: "NSB GameDay+ brings our FlightScope and Stalker Pro II hardware to your games and events. Organizations can book Team Data Capture ($500), Exposure Engine ($1,500+), or Full Broadcast ($5,000+). Parents can purchase individual Data Packs starting at $99." },
    { q: "How does NSB help with college placement?", a: "We push your verified data into the NSB Scout Wire, visible to 6,000+ college coaches. Our scouts work directly with coaching staffs to match your profile with the right programs and opportunities." },
  ];

  const testimonials = [
    { name: "Marcus T., Parent", quote: "NSB gave my son the verified data and exposure he needed. Within 3 months of his evaluation, he had college coaches reaching out. Worth every penny.", location: "Baton Rouge, LA" },
    { name: "Jake R., 2025 RHP", quote: "The 20-80 report from NSB's scouts was a game-changer. Coaches took my profile seriously because it came from former MLB evaluators. Committed to my dream school.", location: "Hattiesburg, MS" },
    { name: "Coach Davis, D2 Head Coach", quote: "When I see an NSB-verified prospect, I know the data is legit. FlightScope metrics from professional scouts \u2014 that's the gold standard in our evaluation process.", location: "Alabama" },
  ];

  const scrollToSection = (href) => {
    setMobileMenuOpen(false);
    if (href.startsWith("#")) {
      const el = document.getElementById(href.replace("#", ""));
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden" data-testid="landing-page">
      {/* Background */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#020817] via-[#0a0e27] to-black" />
        <div className="stars-layer" />
      </div>

      {/* STICKY NAVIGATION */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-white/10 backdrop-blur-xl bg-black/70" data-testid="nav-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <Link to="/" className="flex items-center gap-2 flex-shrink-0">
              <NSBLogo className="h-8 sm:h-10" />
            </Link>
            <div className="hidden lg:flex items-center gap-1">
              {navTabs.map((tab) => (
                tab.external ? (
                  <a key={tab.label} href={tab.href} target="_blank" rel="noopener noreferrer"
                    className="px-3 py-1.5 text-xs font-medium text-white/60 hover:text-white hover:bg-white/5 rounded-md transition" data-testid={`nav-${tab.label.toLowerCase().replace(/\s/g,'-')}`}>
                    {tab.label}
                  </a>
                ) : tab.internal ? (
                  <Link key={tab.label} to={tab.href}
                    className="px-3 py-1.5 text-xs font-medium text-white/60 hover:text-white hover:bg-white/5 rounded-md transition" data-testid={`nav-${tab.label.toLowerCase()}`}>
                    {tab.label}
                  </Link>
                ) : (
                  <button key={tab.label} onClick={() => scrollToSection(tab.href)}
                    className="px-3 py-1.5 text-xs font-medium text-white/60 hover:text-white hover:bg-white/5 rounded-md transition" data-testid={`nav-${tab.label.toLowerCase()}`}>
                    {tab.label}
                  </button>
                )
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Link to="/login?mode=player" className="hidden sm:inline-flex text-xs text-white/60 hover:text-white transition px-3 py-1.5" data-testid="nav-player-login-link">
                Player Login
              </Link>
              <Link to="/login" data-testid="nav-coach-login-link">
                <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10 text-xs h-8">
                  Coach Login
                </Button>
              </Link>
              <button className="lg:hidden ml-1 p-1.5 rounded-md hover:bg-white/10 transition" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
                {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl max-h-[70vh] overflow-y-auto">
            <div className="px-4 py-3 space-y-1">
              {navTabs.map((tab) => (
                tab.external ? (
                  <a key={tab.label} href={tab.href} target="_blank" rel="noopener noreferrer"
                    className="flex items-center justify-between px-3 py-2.5 text-sm text-white/70 hover:text-white hover:bg-white/5 rounded-lg transition">
                    {tab.label} <ExternalLink size={14} className="text-white/30" />
                  </a>
                ) : tab.internal ? (
                  <Link key={tab.label} to={tab.href} onClick={() => setMobileMenuOpen(false)}
                    className="block px-3 py-2.5 text-sm text-white/70 hover:text-white hover:bg-white/5 rounded-lg transition">
                    {tab.label}
                  </Link>
                ) : (
                  <button key={tab.label} onClick={() => scrollToSection(tab.href)}
                    className="block w-full text-left px-3 py-2.5 text-sm text-white/70 hover:text-white hover:bg-white/5 rounded-lg transition">
                    {tab.label}
                  </button>
                )
              ))}
              <div className="pt-2 border-t border-white/10">
                <Link to="/login?mode=player" onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-2.5 text-sm text-white/70 hover:text-white">Player Login</Link>
                <Link to="/login?mode=admin" onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-2.5 text-sm text-white/70 hover:text-white">Administrative Login</Link>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* ========== SECTION 1: HERO + NEWS DESK SPLIT ========== */}
      <section className="relative z-10 min-h-screen pt-14" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-transparent z-10" />
        <img
          src="https://images.pexels.com/photos/163487/baseball-player-pitcher-ball-163487.jpeg?auto=compress&cs=tinysrgb&w=1920"
          alt="" className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="absolute top-1/4 right-0 w-1/2 h-px bg-gradient-to-l from-red-500/50 to-transparent z-10" />
        <div className="absolute top-1/3 right-10 w-1/3 h-px bg-gradient-to-l from-blue-500/30 to-transparent z-10" />

        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20 lg:py-24">
          <div className="grid lg:grid-cols-[1fr_520px] gap-8 lg:gap-10 items-start">
            {/* LEFT: Hero Content */}
            <div className="pt-4 sm:pt-8">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-red-500/30 bg-red-500/10 mb-6">
                <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                <span className="text-xs sm:text-sm text-red-300 tracking-wider uppercase">National Scouting Bureau</span>
              </div>

              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black leading-[0.95] mb-4 tracking-tight" data-testid="hero-headline">
                EVERY ATHLETE<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-red-400 to-orange-400">NEEDS A</span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-orange-400"> ROADMAP.</span><br />
                <span className="text-white/60 text-xl sm:text-3xl lg:text-4xl">EVERY PARENT NEEDS </span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-yellow-300 text-xl sm:text-3xl lg:text-4xl">GUIDANCE.</span>
              </h1>

              <p className="text-base sm:text-lg text-white/70 max-w-xl mb-2 leading-relaxed font-medium" data-testid="hero-subhead">
                Whether managing academics, developing skills and game IQ, or navigating exposure &amp; recruiting — the National Scouting Bureau is your centralized source.
              </p>
              <p className="text-sm text-white/50 max-w-xl mb-4 leading-relaxed">
                Verified scouting. Real recruiting exposure. Earn up to 12,000 Tuition Rewards points per year — guaranteed minimum scholarships at 400+ colleges through the SAGE Scholars Tuition Rewards&reg; Program — included at no cost to families.
              </p>

              {/* 4 Pillars */}
              <div className="grid grid-cols-2 gap-2 max-w-md mb-6">
                {[
                  { icon: <GraduationCap size={18} />, label: "Academics", color: "text-amber-400 border-amber-500/30 bg-amber-500/10" },
                  { icon: <Target size={18} />, label: "Skill / IQ", color: "text-red-400 border-red-500/30 bg-red-500/10" },
                  { icon: <Eye size={18} />, label: "Exposure", color: "text-blue-400 border-blue-500/30 bg-blue-500/10" },
                  { icon: <Users size={18} />, label: "Recruitment", color: "text-green-400 border-green-500/30 bg-green-500/10" },
                ].map((p) => (
                  <div key={p.label} className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${p.color}`}>
                    {p.icon}
                    <span className="text-xs sm:text-sm font-bold text-white">{p.label}</span>
                  </div>
                ))}
              </div>

              <p className="text-sm text-white/40 mb-5">
                <span className="text-red-400 font-black">600+</span> Placements · <span className="text-amber-400 font-black">20+</span> MLB Draft Picks · <span className="text-blue-400 font-black">12,000</span> Tuition Rewards pts/yr
              </p>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <a href={`${EXT_BASE}/free`} target="_blank" rel="noopener noreferrer" data-testid="hero-free-cta">
                  <Button className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base rounded-lg shadow-[0_0_30px_rgba(220,38,38,0.3)] border border-red-500/30 w-full sm:w-auto">
                    <UserPlus className="w-5 h-5 mr-2" />
                    Join NSB Connect — Free
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </a>
                <a href="#memberships" onClick={(e) => { e.preventDefault(); document.getElementById('memberships')?.scrollIntoView({ behavior: 'smooth' }); }} data-testid="hero-memberships-cta">
                  <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base rounded-lg w-full sm:w-auto">
                    <Crown className="w-5 h-5 mr-2" />
                    Explore Memberships
                  </Button>
                </a>
              </div>
            </div>

            {/* RIGHT: NSB ScoutWire News */}
            <div className="rounded-xl border border-white/10 bg-black/60 backdrop-blur-xl overflow-hidden" data-testid="news-feed-section">
              <div className="px-4 pt-4 pb-3 border-b border-white/5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                    <span className="text-xs font-bold text-white/80 tracking-wider uppercase">NSB ScoutWire News</span>
                  </div>
                  <Link to="/newsdesk" className="text-[10px] text-red-400 hover:text-red-300 font-medium flex items-center gap-0.5 transition" data-testid="view-all-news-btn">
                    Full ScoutWire <ArrowRight size={10} />
                  </Link>
                </div>
                <div className="flex flex-wrap gap-1" data-testid="news-division-tabs">
                  {newsDivisions.map((div) => {
                    const isActive = activeNewsDivision === div;
                    const colorMap = { "Inside NSB": "bg-white-600", "High School": "bg-orange-600", "BC9 HBCU": "bg-teal-600", "D1": "bg-red-600", "D2": "bg-blue-600", "D3": "bg-amber-600", "NAIA": "bg-green-600", "JUCO": "bg-purple-600" };
                    const borderMap = { "Inside NSB": "text-white/70", "High School": "text-orange-400/70", "BC9 HBCU": "text-teal-400/70", "D1": "text-red-400/70", "D2": "text-blue-400/70", "D3": "text-amber-400/70", "NAIA": "text-green-400/70", "JUCO": "text-purple-400/70" };
                    return (
                      <button key={div} onClick={() => setActiveNewsDivision(div)}
                        className={`px-2 py-1 rounded text-[10px] font-bold transition-all ${isActive ? `${colorMap[div]} text-white` : `${borderMap[div]} hover:bg-white/5`}`}
                        data-testid={`news-tab-${div.toLowerCase().replace(/\s/g, '-')}`}>
                        {div}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="divide-y divide-white/5 max-h-[420px] overflow-y-auto news-scroll">
                {(newsFeed[activeNewsDivision] || []).map((article, i) => (
                  <Link key={article.post_id} to={`/blog/${article.slug || article.post_id}`}
                    className="flex gap-3 p-3 hover:bg-white/[0.04] transition-all group" data-testid={`news-card-${i}`}>
                    <div className="w-20 h-14 rounded-md overflow-hidden flex-shrink-0 bg-white/5">
                      {article.featured_image_url ? (
                        <img src={article.featured_image_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><Newspaper className="w-5 h-5 text-white/15" /></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-xs sm:text-sm font-bold text-white group-hover:text-red-400 transition leading-tight line-clamp-2">{article.title}</h4>
                      <div className="flex items-center gap-2 mt-1 text-[10px] text-white/30">
                        <Calendar className="w-2.5 h-2.5" />
                        {new Date(article.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                        <span className="text-white/10">|</span>
                        <span>{article.author_name}</span>
                      </div>
                    </div>
                  </Link>
                ))}
                {(!newsFeed[activeNewsDivision] || newsFeed[activeNewsDivision].length === 0) && (
                  <div className="text-center py-10">
                    <Newspaper className="w-8 h-8 text-white/10 mx-auto mb-2" />
                    <p className="text-white/30 text-xs">No articles yet for {activeNewsDivision}.</p>
                  </div>
                )}
              </div>
              {(newsFeed[activeNewsDivision] || []).length > 0 && (
                <div className="border-t border-white/5 px-4 py-2">
                  <Link to={`/newsdesk?division=${encodeURIComponent(activeNewsDivision)}`} className="text-[10px] text-white/40 hover:text-white flex items-center justify-center gap-1 transition" data-testid="view-division-news-btn">
                    View all {activeNewsDivision} news <ChevronRight size={10} />
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF BAR */}
      <section className="relative z-10 py-10 sm:py-14 border-t border-white/5 bg-gradient-to-b from-red-500/5 to-transparent" data-testid="social-proof-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
            {[
              { value: "600+", label: "College Placements", color: "text-red-400" },
              { value: "6,000+", label: "College Coaches", color: "text-amber-400" },
              { value: "20+", label: "MLB Draft Picks", color: "text-blue-400" },
              { value: "MLB", label: "Combine-Grade Hardware", color: "text-green-400" },
            ].map((stat) => (
              <div key={stat.label} className="text-center p-4 sm:p-6 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm">
                <div className={`text-2xl sm:text-4xl font-black ${stat.color}`}>{stat.value}</div>
                <div className="text-xs sm:text-sm text-white/50 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== THE 12-MONTH ROADMAP ========== */}
      <section id="roadmap" className="relative z-10 py-16 sm:py-24 border-t border-white/5" data-testid="roadmap-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <span className="text-xs tracking-[0.4em] text-red-400/80 uppercase">The NSB Method</span>
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-black mt-2 tracking-tight">The Scholar-Athlete Timeline</h2>
            <p className="text-sm text-white/50 mt-3 max-w-2xl mx-auto">Every athlete's journey is different. Select a scenario below — or watch the board rearrange automatically to see how NSB adapts to each stage of the recruitment lifecycle.</p>
          </div>

          <TimelineBoard />
        </div>
      </section>

      {/* ========== THE GOLDEN TICKET ========== */}
      <section id="golden-ticket" className="relative z-10 py-16 sm:py-24 border-t border-white/5" data-testid="golden-ticket-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <span className="text-xs tracking-[0.4em] text-amber-400/80 uppercase">The NSB Golden Ticket</span>
            <h2 className="text-2xl sm:text-4xl font-black mt-2 tracking-tight">Your Membership Pays for Itself</h2>
          </div>

          <div className="rounded-2xl border-2 border-amber-500/40 bg-gradient-to-b from-amber-500/10 via-amber-500/5 to-transparent p-1 shadow-[0_0_60px_rgba(245,158,11,0.1)]" data-testid="golden-ticket-card">
            <div className="rounded-xl bg-gradient-to-br from-[#0c1020] to-[#0a0e1a] p-6 sm:p-10">
              {/* Header */}
              <div className="flex items-center justify-center gap-3 mb-8">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent to-amber-500/30" />
                <div className="flex items-center gap-2 px-4">
                  <Crown size={20} className="text-amber-400" />
                  <span className="text-sm font-black text-amber-400 tracking-wider uppercase">What's Inside Every Premium Membership</span>
                  <Crown size={20} className="text-amber-400" />
                </div>
                <div className="h-px flex-1 bg-gradient-to-l from-transparent to-amber-500/30" />
              </div>

              {/* Two Pillars Side by Side */}
              <div className="grid sm:grid-cols-2 gap-6">
                {/* Combine Access */}
                <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center shadow-lg">
                      <Target size={24} className="text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-black text-white">Combine & Camp Access</h3>
                      <p className="text-xs text-white/40">FlightScope + Stalker Pro II verified</p>
                    </div>
                  </div>
                  <div className="space-y-2.5">
                    {[
                      { bold: "2–6+", text: "Combine/Camp credits per year (by tier)" },
                      { bold: "Hardware", text: "MLB-grade FlightScope data at every event" },
                      { bold: "Priority", text: "Guaranteed booking before public registration" },
                      { bold: "Verified", text: "NSB Verified Badge upon completion" },
                    ].map((item) => (
                      <div key={item.text} className="flex items-start gap-2">
                        <Check size={14} className="text-red-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-white/70"><span className="text-white font-bold">{item.bold}</span> — {item.text}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-center">
                    <span className="text-xs text-red-300">A single NSB Combine costs <span className="font-black text-white">$150–$500</span> à la carte</span>
                  </div>
                </div>

                {/* Tuition Rewards */}
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center shadow-lg">
                      <GraduationCap size={24} className="text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-black text-white">Tuition Rewards</h3>
                      <p className="text-xs text-white/40">Powered by SAGE Scholars</p>
                    </div>
                  </div>
                  <div className="space-y-2.5">
                    {[
                      { bold: "Up to 12,000", text: "points per year (guaranteed minimum scholarships)" },
                      { bold: "400+", text: "participating colleges nationwide" },
                      { bold: "Never expire", text: "Points accumulate as long as membership is held" },
                      { bold: "Transferable", text: "Points can transfer to eligible family members" },
                    ].map((item) => (
                      <div key={item.text} className="flex items-start gap-2">
                        <Check size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-white/70"><span className="text-white font-bold">{item.bold}</span> — {item.text}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-center">
                    <span className="text-xs text-amber-300">Over 4 years: up to <span className="font-black text-white">48,000 points</span> in guaranteed minimum tuition discounts</span>
                  </div>
                </div>
              </div>

              {/* Bottom CTA */}
              <div className="mt-8 text-center">
                <p className="text-sm text-white/50 mb-4">When you add up the Combine access + Tuition Rewards savings, the membership more than pays for itself.</p>
                <a href="#memberships" onClick={(e) => { e.preventDefault(); document.getElementById('memberships')?.scrollIntoView({ behavior: 'smooth' }); }}>
                  <Button className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-bold px-8 py-5 text-sm sm:text-base rounded-lg shadow-[0_0_30px_rgba(245,158,11,0.2)]" data-testid="golden-ticket-cta">
                    <Crown className="w-5 h-5 mr-2" />
                    See Membership Tiers
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SECTION 2: NSB CONNECT FREE TIER ========== */}
      <section id="free-tier" className="relative z-10 py-16 sm:py-24 border-t border-white/5" data-testid="free-tier-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="rounded-2xl border border-green-500/20 bg-gradient-to-b from-green-500/5 to-transparent p-8 sm:p-12 relative overflow-hidden">
            <div className="text-center sm:text-left">
              <div className="mb-3">
                <span className="px-3 py-1.5 bg-green-500/20 border border-green-500/30 text-green-400 text-[10px] sm:text-xs font-bold rounded-full uppercase tracking-wider">Limited to first 5,000 athletes</span>
              </div>
              <span className="text-xs tracking-[0.4em] text-green-400/80 uppercase">The Red Napkin — Your Free Starting Point</span>
              <h2 className="text-2xl sm:text-4xl lg:text-5xl font-black mt-2 tracking-tight" data-testid="free-tier-headline">
                Start With NSB Connect — <span className="text-green-400">$0</span>
              </h2>
              <p className="text-sm sm:text-base text-white/50 mt-3 max-w-xl">
                Get your foot in the door. No credit card. No commitment. Build your profile, upload your first highlight, and see where your journey leads.
              </p>
            </div>

            <div className="grid sm:grid-cols-3 gap-4 mt-8">
              {[
                { icon: <Eye className="w-6 h-6" />, text: "Profile visible to college coaches" },
                { icon: <Gift className="w-6 h-6" />, text: "100 Tuition Rewards points/mo (1,200/yr)" },
                { icon: <Video className="w-6 h-6" />, text: "1 highlight video (20 sec)" },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10">
                  <div className="text-green-400 flex-shrink-0">{item.icon}</div>
                  <span className="text-sm text-white/70 font-medium">{item.text}</span>
                </div>
              ))}
            </div>

            {/* Onboarding Progress Tracker */}
            <div className="mt-8 rounded-xl bg-white/[0.03] border border-white/10 p-5" data-testid="onboarding-tracker">
              <h4 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
                <Layers size={16} className="text-green-400" />
                Your Recruiting Journey Starts Here
              </h4>
              <div className="flex flex-col sm:flex-row gap-3">
                {[
                  { step: "1", label: "Build Free Profile", status: "complete", icon: <UserPlus size={16} />, color: "border-green-500/40 bg-green-500/10", textColor: "text-green-400", statusLabel: "Immediate" },
                  { step: "2", label: "Upload First Highlight Reel", status: "pending", icon: <Video size={16} />, color: "border-amber-500/30 bg-amber-500/5", textColor: "text-amber-400", statusLabel: "Unlocked" },
                  { step: "3", label: "Secure Your Regional Combine Spot", status: "locked", icon: <Target size={16} />, color: "border-white/10 bg-white/[0.02]", textColor: "text-white/30", statusLabel: "Premium" },
                ].map((s, i) => (
                  <div key={s.step} className="flex-1 flex items-stretch gap-0">
                    <div className={`flex-1 rounded-xl border ${s.color} p-4 relative`}>
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black ${
                          s.status === "complete" ? "bg-green-500 text-white" :
                          s.status === "pending" ? "bg-amber-500/20 text-amber-400 border border-amber-500/40" :
                          "bg-white/10 text-white/30"
                        }`}>
                          {s.status === "complete" ? <Check size={12} /> : s.step}
                        </div>
                        <span className={`text-[10px] font-bold uppercase tracking-wider ${
                          s.status === "complete" ? "text-green-400" :
                          s.status === "pending" ? "text-amber-400" : "text-white/30"
                        }`}>{s.statusLabel}</span>
                      </div>
                      <div className={`${s.textColor} mb-1`}>{s.icon}</div>
                      <p className={`text-sm font-medium ${s.status === "locked" ? "text-white/30" : "text-white"}`}>{s.label}</p>
                      {s.status === "locked" && (
                        <div className="absolute top-3 right-3">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 text-white/20"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
                        </div>
                      )}
                    </div>
                    {i < 2 && <div className="hidden sm:flex items-center px-1"><ChevronRight size={16} className="text-white/15" /></div>}
                  </div>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-4 mt-6">
              <a href={`${EXT_BASE}/free`} target="_blank" rel="noopener noreferrer" data-testid="free-tier-cta" className="block">
                <Button className="bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg shadow-[0_0_25px_rgba(34,197,94,0.2)] border border-green-500/30 w-full py-4">
                  <UserPlus className="w-4 h-4 mr-1.5" />
                  Claim Your Free Spot
                  <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </a>
              <a href="https://www.tuitionrewards.com/colleges" target="_blank" rel="noopener noreferrer" data-testid="participating-colleges-link" className="block">
                <Button variant="outline" className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 text-xs rounded-lg w-full py-4">
                  <GraduationCap className="w-4 h-4 mr-1" />
                  Participating Colleges
                </Button>
              </a>
              <Link to="/tuition-colleges" data-testid="nsb-baseball-schools-link" className="block">
                <Button variant="outline" className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10 text-xs rounded-lg w-full py-4">
                  <GraduationCap className="w-4 h-4 mr-1" />
                  Tuition Rewards Baseball Schools
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SECTION 3: NSB UNIVERSE MEMBERSHIPS ========== */}
      <section id="memberships" className="relative z-10 py-16 sm:py-24 border-t border-white/5" data-testid="memberships-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <span className="text-xs tracking-[0.4em] text-amber-400/80 uppercase">NSB Universe</span>
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-black mt-2 tracking-tight" data-testid="memberships-headline">NSB Universe Memberships</h2>
            <p className="text-sm text-white/50 mt-3 max-w-2xl mx-auto">Every premium tier includes guaranteed Combine &amp; Camp access with FlightScope-verified data. Your 12-month service agreement ensures year-round access to the full NSB ecosystem — tailored to your athlete's stage.</p>
          </div>

          <div className="grid sm:grid-cols-3 gap-5 sm:gap-6 max-w-5xl mx-auto">
            {[
              {
                name: "Plus", price: "$99", period: "/mo*", annual: "$1,188/yr", color: "border-blue-500/30", ring: "",
                badge: null,
                rewards: "3,000", rewardsPer: "250/mo",
                combines: "2", campLabel: "Combine/Camp credits",
                items: ["NSB Connect included", "Academy + University access", "20% evaluation discount", "2 Combine/Camp credits per year", "Priority email support", "Tuition Rewards tracking"],
                href: `${EXT_BASE}/membership`, ctaText: "Get Started Here", ctaClass: "bg-blue-600 hover:bg-blue-700 text-white",
              },
              {
                name: "Elite", price: "$179", period: "/mo*", annual: "$2,148/yr", color: "border-amber-500/30", ring: "ring-1 ring-amber-500/40 shadow-[0_0_40px_rgba(245,158,11,0.08)]",
                badge: "MOST POPULAR",
                rewards: "6,000", rewardsPer: "500/mo",
                combines: "4", campLabel: "Combine/Camp credits",
                items: ["Everything in Plus", "4 Combine/Camp credits per year", "Priority booking at all events", "Expanded video hosting", "Direct scout messaging", "Advanced analytics"],
                href: `${EXT_BASE}/membership`, ctaText: "Get Started Here", ctaClass: "bg-amber-500 hover:bg-amber-600 text-black font-bold",
              },
              {
                name: "Platinum", price: "$249", period: "/mo*", annual: "$2,988/yr", color: "border-red-500/30", ring: "ring-1 ring-red-500/40 shadow-[0_0_40px_rgba(220,38,38,0.1)]",
                badge: "VIP",
                rewards: "12,000", rewardsPer: "1,000/mo",
                combines: "6+", campLabel: "Combine/Camp credits",
                items: ["Everything in Elite", "6+ Combine/Camp credits per year", "VIP access to all events", "Unlimited video hosting", "Personal scout advisor", "College coach introductions"],
                href: `${EXT_BASE}/membership`, ctaText: "Get Started Here", ctaClass: "bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold",
              },
            ].map((plan) => (
              <div key={plan.name} className={`relative rounded-xl border ${plan.color} bg-gradient-to-b from-white/5 to-transparent p-6 sm:p-7 ${plan.ring}`}>
                {plan.badge && (
                  <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 text-xs font-bold rounded-full tracking-wider ${plan.badge === "VIP" ? 'bg-gradient-to-r from-red-600 to-red-700 text-white' : 'bg-amber-500 text-black'}`}>{plan.badge}</div>
                )}
                {/* Tuition Rewards Lead */}
                <div className="text-center mb-4 pt-2 pb-3 border-b border-white/10">
                  <div className="flex items-center justify-center gap-1.5 mb-1">
                    <GraduationCap size={16} className="text-amber-400" />
                    <span className="text-xs text-amber-400 font-bold uppercase tracking-wider">Tuition Rewards</span>
                  </div>
                  <div className="text-2xl sm:text-3xl font-black text-amber-400">{plan.rewards}<span className="text-sm text-amber-400/60"> pts/yr</span></div>
                  <div className="text-[11px] text-white/40">{plan.rewardsPer} pts · guaranteed minimum scholarship</div>
                </div>
                {/* Pricing */}
                <div className="text-center mb-2">
                  <h3 className="text-lg font-bold text-white">{plan.name}</h3>
                  <div className="font-black text-white mt-1">
                    <span className="text-3xl sm:text-4xl">{plan.price}</span>
                    <span className="text-sm text-white/40">{plan.period}</span>
                  </div>
                  <div className="text-xs text-white/30 mt-0.5">or {plan.annual} billed annually</div>
                </div>
                {/* Combine badge */}
                <div className="flex items-center justify-center gap-2 mb-4 py-2 rounded-lg bg-white/5 border border-white/10">
                  <Target size={14} className="text-red-400" />
                  <span className="text-xs text-white/60 font-medium">{plan.combines} {plan.campLabel}/year</span>
                </div>
                <ul className="space-y-2.5 mb-6">
                  {plan.items.map(item => (
                    <li key={item} className="flex items-start gap-2 text-sm text-white/70">
                      <Check size={14} className="text-green-400 flex-shrink-0 mt-0.5" /> {item}
                    </li>
                  ))}
                </ul>
                <a href={plan.href} target="_blank" rel="noopener noreferrer" className="block">
                  <Button className={`w-full text-xs ${plan.ctaClass}`} data-testid={`membership-cta-${plan.name.toLowerCase()}`}>
                    {plan.ctaText} <ArrowRight size={14} className="ml-1.5" />
                  </Button>
                </a>
                {/* Commitment note */}
                <p className="text-[10px] text-white/25 text-center mt-3">*12-month service agreement. Ensures year-round access to combines, camps &amp; scouting services.</p>
              </div>
            ))}
          </div>

          <p className="text-center text-xs sm:text-sm text-white/40 mt-8 max-w-2xl mx-auto">
            All tiers include Tuition Rewards points (guaranteed minimum tuition discounts) tracked via our partner <span className="text-amber-400 font-semibold">SAGE Scholars</span>.
          </p>
        </div>
      </section>

      {/* ========== NSB TUITION REWARDS PROGRAM — POWERED BY SAGE ========== */}
      <section id="tuition-rewards" className="relative z-10 py-16 sm:py-24 border-t border-white/5" data-testid="tuition-rewards-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 mb-4">
              <GraduationCap size={14} className="text-amber-400" />
              <span className="text-xs text-amber-300 tracking-wider uppercase font-bold">Powered by SAGE Scholars</span>
            </div>
            <h2 className="text-2xl sm:text-4xl font-black tracking-tight" data-testid="tuition-rewards-headline">NSB Tuition Rewards Program</h2>
            <p className="text-sm text-white/50 mt-3 max-w-2xl mx-auto">
              Every NSB member earns Tuition Rewards points — guaranteed minimum scholarships at 400+ participating colleges.
            </p>
            <a href="/NSB_Tuition_Rewards_Brochure.pdf" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-3 text-sm text-amber-400 hover:text-amber-300 font-medium transition" data-testid="tuition-brochure-link">
              <FileText size={14} />
              About the NSB Tuition Rewards Program
              <ExternalLink size={12} />
            </a>
          </div>

          {/* Myth vs Fact — Stacked Accordion */}
          <div className="rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.03] to-transparent p-6 sm:p-8 mb-8">
            <h3 className="text-lg sm:text-xl font-bold text-center mb-2">NSB Fusion&#8482; Tuition Rewards: Myth vs. Fact</h3>
            <p className="text-xs text-white/40 text-center mb-6">Understanding Tuition Rewards and what they can mean for your family</p>

            <div className="space-y-3" data-testid="myth-fact-accordion">
              {[
                {
                  myth: "Tuition Rewards are just discount codes that expire.",
                  fact: "Tuition Rewards points never expire. They accumulate over time and represent a guaranteed minimum scholarship — one point equals one dollar in guaranteed minimum tuition discounts — at any of 400+ participating colleges. Points can be transferred to other eligible family members."
                },
                {
                  myth: "Only certain students qualify for the rewards.",
                  fact: "Every NSB member earns Tuition Rewards automatically. There is no GPA requirement, no test score minimum, and no separate application."
                },
                {
                  myth: "The savings are too small to matter.",
                  fact: "Platinum members earn up to 12,000 Tuition Rewards points per year — that's up to 48,000 points over four years of membership. Each point represents one dollar in guaranteed minimum tuition discounts at participating colleges, up to a maximum of one full year of tuition, spread evenly across four years of undergraduate study."
                },
                {
                  myth: "You can only use them at small, unknown schools.",
                  fact: "400+ colleges participate, including NCAA Division 1, 2, and 3 programs, NAIA schools, and well-known universities across 43 states."
                },
                {
                  myth: "It replaces other financial aid or scholarships.",
                  fact: "Participating colleges reserve the right to include Tuition Rewards as part of, or separate from, any financial aid or scholarship package. What is guaranteed is that a student will receive institutional aid at least equal to the value of their submitted points — they may receive more, but never less."
                },
              ].map((item, i) => {
                const isOpen = openFaq === `myth-${i}`;
                return (
                  <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid={`myth-${i}`}>
                    <button
                      className="w-full flex items-center gap-3 p-4 sm:p-5 text-left hover:bg-white/[0.03] transition"
                      onClick={() => setOpenFaq(isOpen ? null : `myth-${i}`)}
                    >
                      <X size={16} className="text-red-400 flex-shrink-0" />
                      <span className="text-[10px] text-red-400 font-bold uppercase tracking-wider mr-1 flex-shrink-0">Myth:</span>
                      <span className="text-sm font-medium text-white/80 flex-1">{item.myth}</span>
                      <ChevronDown size={16} className={`text-white/30 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isOpen && (
                      <div className="px-4 sm:px-5 pb-4 sm:pb-5 border-t border-white/5">
                        <div className="flex items-start gap-3 mt-3 p-3 rounded-lg bg-green-500/10 border border-green-500/15">
                          <Check size={16} className="text-green-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <span className="text-[10px] text-green-400 font-bold uppercase tracking-wider block mb-1">Fact</span>
                            <p className="text-sm text-white/70 leading-relaxed">{item.fact}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="text-center">
            <a href={`${EXT_BASE}/free`} target="_blank" rel="noopener noreferrer">
              <Button className="bg-amber-500 hover:bg-amber-600 text-black font-bold px-6 py-4 text-sm rounded-lg">
                <GraduationCap className="w-4 h-4 mr-2" />
                Start Earning Tuition Rewards — Join Free
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>

          {/* ========== MARKET EXCLUSIVITY NOTICE ========== */}
          <div className="mt-16 sm:mt-20" data-testid="market-exclusivity-notice">
            <div className="relative rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.08] via-black/40 to-red-600/[0.05] p-6 sm:p-10 overflow-hidden">
              {/* Decorative seal */}
              <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-amber-500/5 blur-3xl pointer-events-none" />
              <div className="absolute -bottom-12 -left-12 w-48 h-48 rounded-full bg-red-600/5 blur-3xl pointer-events-none" />

              <div className="relative">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full border border-amber-500/40 bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                      <ShieldCheck className="w-6 h-6 text-amber-400" />
                    </div>
                    <div>
                      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-amber-500/40 bg-amber-500/10 mb-1.5">
                        <span className="text-[10px] tracking-[0.25em] text-amber-300 uppercase font-bold">Exclusive Strategic Partnership</span>
                      </div>
                      <h3 className="text-xl sm:text-2xl font-black tracking-tight text-white" data-testid="exclusivity-headline">
                        Market Exclusivity Notice
                      </h3>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 text-sm sm:text-base text-white/75 leading-relaxed max-w-3xl">
                  <p>
                    The <span className="text-amber-300 font-semibold">National Scouting Bureau</span> holds <span className="text-white font-semibold">exclusive integration rights</span> with the <span className="text-amber-300 font-semibold">SAGE Scholars Tuition Rewards&reg; Program</span> across the verified athletic evaluation category — the only platform authorized to merge FlightScope-verified scouting data with guaranteed minimum tuition scholarships at 400+ participating colleges.
                  </p>
                  <p>
                    Our proprietary <span className="text-white font-semibold">NSB Fusion&trade; Tuition Rewards architecture</span>, <span className="text-white font-semibold">12-Month Scholar-Athlete Roadmap&trade;</span>, and <span className="text-white font-semibold">NSB Verified&trade; progressive authentication framework</span> are protected methodologies — <span className="text-amber-300 font-semibold">unavailable</span> to competing scouting services, showcase organizations, or recruiting platforms.
                  </p>
                  <p className="text-white/60 text-sm border-l-2 border-amber-500/40 pl-4 italic">
                    Access to the NSB ecosystem — including co-branded recruiting placement, verified data licensing, and tuition reward distribution — is granted exclusively through direct strategic partnership. Organizations seeking to align with the gold standard in scholar-athlete development partner with NSB by invitation only.
                  </p>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center">
                  <a href="mailto:partnerships@nationalscoutingbureau.com?subject=Strategic%20Partnership%20Inquiry" data-testid="partnership-inquiry-cta">
                    <Button className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-bold px-6 py-4 text-sm rounded-lg shadow-[0_0_30px_rgba(245,158,11,0.25)]">
                      <Shield className="w-4 h-4 mr-2" />
                      Request Partnership Access
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </a>
                  <p className="text-xs text-white/40">
                    Limited partnership slots reviewed quarterly. <span className="text-amber-400/80">partnerships@nationalscoutingbureau.com</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SECTION 4: INDIVIDUAL EVALUATIONS ========== */}
      <section id="evaluations" className="relative z-10 py-16 sm:py-24 border-t border-white/5" data-testid="evaluations-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <span className="text-xs tracking-[0.4em] text-red-400/80 uppercase">Individual Evaluation</span>
            <h2 className="text-2xl sm:text-4xl font-black mt-2 tracking-tight" data-testid="evaluations-headline">Get Your Individual Evaluation</h2>
            <p className="text-sm sm:text-base text-white/50 mt-2">Choose Your Evaluation Tier</p>
            <p className="text-xs text-white/40 mt-1 max-w-xl mx-auto">Every tier is powered by FlightScope technology and evaluated by former MLB scouts.</p>
          </div>

          <div className="grid sm:grid-cols-3 gap-5 sm:gap-6 max-w-4xl mx-auto">
            {[
              {
                name: "Data Report", price: "$99", color: "border-white/10",
                items: ["Exit Velo & Launch Angle", "Spin Rate & Extension", "FlightScope Verified", "Digital Report Card", "NSB Profile Listing"],
                href: `${EXT_BASE}`, ctaText: "Book Now",
              },
              {
                name: "Data + Film", price: "$200", color: "border-amber-500/30", popular: true,
                items: ["Everything in Data Report", "3-Angle Recruit Clips", "Highlight Reel", "CoachTube Ready", "Shareable Profile Link"],
                href: `${EXT_BASE}`, ctaText: "Book Now",
              },
              {
                name: "VPP", price: "$500", color: "border-red-500/30", elite: true,
                items: ["Everything in Data + Film", "Full Scout Report (20-80)", "College Coach Outreach", "Recruiting Strategy Call", "NSB Verified Badge"],
                href: `${EXT_BASE}`, ctaText: "Book Now",
              },
            ].map((plan) => (
              <div key={plan.name} className={`relative rounded-xl border ${plan.color} bg-gradient-to-b from-white/5 to-transparent p-5 sm:p-6 ${plan.popular ? 'ring-1 ring-amber-500/40 shadow-[0_0_40px_rgba(245,158,11,0.08)]' : ''} ${plan.elite ? 'ring-1 ring-red-500/40 shadow-[0_0_40px_rgba(220,38,38,0.1)]' : ''}`}>
                {plan.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-amber-500 text-black text-xs font-bold rounded-full tracking-wider">MOST POPULAR</div>}
                {plan.elite && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-red-600 to-red-700 text-white text-xs font-bold rounded-full tracking-wider">VERIFIED PLAYER PROFILE</div>}
                <div className="text-center mb-5 pt-1">
                  <h3 className="text-lg font-bold text-white">{plan.name}</h3>
                  <div className="text-3xl sm:text-4xl font-black text-white mt-2">{plan.price}</div>
                </div>
                <ul className="space-y-2.5 mb-6">
                  {plan.items.map(item => (
                    <li key={item} className="flex items-start gap-2 text-sm text-white/70">
                      <Check size={14} className="text-red-400 flex-shrink-0 mt-0.5" /> {item}
                    </li>
                  ))}
                </ul>
                <a href={plan.href} target="_blank" rel="noopener noreferrer" className="block">
                  <Button className={`w-full text-sm ${plan.popular ? 'bg-amber-500 hover:bg-amber-600 text-black font-bold' : plan.elite ? 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold' : 'bg-white/10 hover:bg-white/15 text-white'}`} data-testid={`eval-cta-${plan.name.toLowerCase().replace(/\s/g,'-')}`}>
                    {plan.ctaText} <ArrowRight size={14} className="ml-1.5" />
                  </Button>
                </a>
              </div>
            ))}
          </div>

          <div className="text-center mt-8 space-y-3">
            <p className="text-sm text-amber-400 font-semibold">NSB Universe members save 20% on all evaluation tiers</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href={`${EXT_BASE}`} target="_blank" rel="noopener noreferrer" data-testid="eval-book-cta">
                <Button className="bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-3 text-sm">
                  <BarChart3 className="w-4 h-4 mr-2" /> Book Your Evaluation
                </Button>
              </a>
              <a href={`${EXT_BASE}/free`} target="_blank" rel="noopener noreferrer" data-testid="eval-free-cta">
                <Button variant="outline" className="border-green-500/30 text-green-400 hover:bg-green-500/10 px-6 py-3 text-sm">
                  <Gift className="w-4 h-4 mr-2" /> Join Free & Save 20%
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SECTION 5: NSB GAMEDAY+ ========== */}
      <section id="gameday-plus" className="relative z-10 py-16 sm:py-24 border-t border-white/5" data-testid="gameday-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <span className="text-xs tracking-[0.4em] text-blue-400/80 uppercase">GameDay+</span>
            <h2 className="text-2xl sm:text-4xl font-black mt-2 tracking-tight" data-testid="gameday-headline">
              NSB GameDay+&#8482; — The System That Scales<br className="hidden sm:block" /> From the Dugout to the Living Room
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* FOR ORGANIZATIONS */}
            <div className="rounded-xl border border-blue-500/20 bg-gradient-to-b from-blue-500/5 to-transparent p-6 sm:p-8" data-testid="gameday-orgs">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">For Organizations</h3>
                  <p className="text-xs text-white/40">Teams, leagues, and event operators</p>
                </div>
              </div>

              <div className="space-y-3">
                {[
                  { level: "Level 1", name: "Team Data Capture", price: "$500", desc: "FlightScope + Stalker Pro II at your games. Every player gets a post-game data report." },
                  { level: "Level 2", name: "Exposure Engine", price: "$1,500+", desc: "Full data capture plus NSB Scout Wire distribution. College coaches see your players' verified metrics." },
                  { level: "Level 3", name: "NSB TV Full Broadcast", price: "$5,000+", desc: "Multi-camera live broadcast, real-time data overlays, and professional commentary. The full NSB production." },
                ].map((tier) => (
                  <div key={tier.level} className="flex items-start gap-4 p-4 rounded-lg bg-white/5 border border-white/10">
                    <div className="text-center flex-shrink-0 w-16">
                      <span className="text-[10px] text-blue-400 font-bold uppercase tracking-wider block">{tier.level}</span>
                      <span className="text-lg font-black text-white">{tier.price}</span>
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-white">{tier.name}</h4>
                      <p className="text-xs text-white/50 mt-1 leading-relaxed">{tier.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6">
                <a href={`${EXT_BASE}/gameday-plus`} target="_blank" rel="noopener noreferrer" data-testid="gameday-org-cta">
                  <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm">
                    <Tv className="w-4 h-4 mr-2" /> Book GameDay+ <ArrowRight size={14} className="ml-1.5" />
                  </Button>
                </a>
              </div>
            </div>

            {/* FOR PARENTS */}
            <div className="rounded-xl border border-amber-500/20 bg-gradient-to-b from-amber-500/5 to-transparent p-6 sm:p-8" data-testid="gameday-parents">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">For Parents</h3>
                  <p className="text-xs text-white/40">Individual player add-on packages</p>
                </div>
              </div>

              <div className="space-y-3">
                {[
                  { name: "Data Pack", price: "$99", items: ["FlightScope verified metrics", "Digital report card", "NSB profile listing"] },
                  { name: "Data + Film", price: "$200", items: ["Everything in Data Pack", "3-angle recruit clips", "Highlight reel"] },
                  { name: "Scouting Report", price: "$500", items: ["Everything in Data + Film", "Full 20-80 scout report", "College coach distribution"] },
                ].map((pkg) => (
                  <div key={pkg.name} className="p-4 rounded-lg bg-white/5 border border-white/10">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-bold text-white">{pkg.name}</h4>
                      <span className="text-lg font-black text-amber-400">{pkg.price}</span>
                    </div>
                    <ul className="space-y-1">
                      {pkg.items.map(item => (
                        <li key={item} className="flex items-center gap-2 text-xs text-white/50">
                          <Check size={12} className="text-amber-400 flex-shrink-0" /> {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <div className="mt-6">
                <a href={`${EXT_BASE}/gameday-plus-packages`} target="_blank" rel="noopener noreferrer" data-testid="gameday-parent-cta">
                  <Button className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold text-sm">
                    View Packages <ArrowRight size={14} className="ml-1.5" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SECTION 6: NSB VERIFIED BADGE ========== */}
      <section id="verified" className="relative z-10 py-16 sm:py-24 border-t border-white/5" data-testid="verified-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="rounded-2xl border border-white/10 bg-gradient-to-b from-white/5 to-transparent p-8 sm:p-12 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-transparent to-red-500/5" />
            <div className="relative z-10">
              <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-blue-500 to-red-500 flex items-center justify-center mb-6 shadow-[0_0_40px_rgba(59,130,246,0.2)]">
                <ShieldCheck className="w-8 h-8 text-white" />
              </div>

              <span className="text-xs tracking-[0.4em] text-blue-400/80 uppercase">Earn Your Badge</span>
              <h2 className="text-2xl sm:text-4xl font-black mt-2 tracking-tight" data-testid="verified-headline">Earn Your NSB Verified Badge</h2>

              <p className="text-sm sm:text-base text-white/50 mt-4 max-w-2xl mx-auto leading-relaxed">
                Attend a Combine, GameDay, or Rep session. Get verified with real hardware data — FlightScope, Stalker Pro II, and more. Your NSB Verified badge appears on your profile, and coaches see verified athletes first.
              </p>

              <div className="grid sm:grid-cols-4 gap-3 mt-8 max-w-3xl mx-auto">
                {[
                  { step: "1", label: "Attend an Event", icon: <Calendar className="w-5 h-5" /> },
                  { step: "2", label: "Get Measured", icon: <Target className="w-5 h-5" /> },
                  { step: "3", label: "Badge Unlocked", icon: <ShieldCheck className="w-5 h-5" /> },
                  { step: "4", label: "Coaches See You First", icon: <Eye className="w-5 h-5" /> },
                ].map((s) => (
                  <div key={s.step} className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <div className="text-white/60 mb-2 flex justify-center">{s.icon}</div>
                    <div className="text-xs font-bold text-white">{s.label}</div>
                  </div>
                ))}
              </div>

              <div className="mt-8">
                <a href={`${EXT_BASE}`} target="_blank" rel="noopener noreferrer" data-testid="verified-events-cta">
                  <Button className="bg-gradient-to-r from-blue-600 to-red-600 hover:from-blue-700 hover:to-red-700 text-white px-8 py-5 text-sm sm:text-base rounded-lg shadow-[0_0_30px_rgba(59,130,246,0.2)]">
                    <Calendar className="w-5 h-5 mr-2" /> See Upcoming Events <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SECTION 7: SOCIAL PROOF / CREDIBILITY ========== */}
      <section className="relative z-10 py-14 sm:py-20 border-t border-white/5" data-testid="credibility-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight mb-4">
            600+ placements. Former & current MLB scouts on staff. MLB, NFL, NHL Combine-grade hardware.
          </h2>
          <p className="text-sm sm:text-base text-white/50 leading-relaxed max-w-2xl mx-auto">
            The scouting industry runs on relationships and reputation. The National Scouting Bureau amplifies both, so every athlete who deserves to be seen gets the chance.
          </p>
        </div>
      </section>

      {/* LEADERBOARD PREVIEW */}
      <section className="relative z-10 py-16 sm:py-20 border-t border-white/5" data-testid="leaderboard-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <span className="text-xs tracking-[0.4em] text-blue-400/80 uppercase">Verified Performance</span>
            <h2 className="text-2xl sm:text-4xl font-black mt-2 tracking-tight">Top Performers</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
            <div className="rounded-xl border border-amber-500/20 bg-gradient-to-b from-amber-500/10 to-transparent p-5 sm:p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center"><Clock className="w-5 h-5 text-amber-400" /></div>
                <div><h3 className="font-bold text-white">60-Yard Dash</h3><p className="text-xs text-white/40">Fastest verified times</p></div>
              </div>
              <div className="space-y-2">
                {topMetrics.sixty_yard?.slice(0, 5).map((p, i) => (
                  <Link to={`/athlete/${p.athlete_id}`} key={p.athlete_id} className="flex items-center justify-between p-2.5 sm:p-3 rounded-lg bg-white/5 hover:bg-white/10 transition group" data-testid={`sixty-player-${i}`}>
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${i === 0 ? 'bg-amber-500 text-black' : i === 1 ? 'bg-gray-400 text-black' : i === 2 ? 'bg-amber-700 text-white' : 'bg-white/10 text-white/60'}`}>{i+1}</span>
                      <div className="min-w-0"><span className="text-sm font-medium text-white group-hover:text-amber-400 transition truncate block">{p.name}</span><span className="text-xs text-white/40 truncate block">{p.school}</span></div>
                    </div>
                    <span className="text-lg font-black text-amber-400 flex-shrink-0 ml-2">{p.metrics?.sixty_time}s</span>
                  </Link>
                ))}
                {topMetrics.sixty_yard?.length === 0 && <div className="text-center py-6 text-white/30 text-sm">No verified times yet</div>}
              </div>
            </div>
            <div className="rounded-xl border border-red-500/20 bg-gradient-to-b from-red-500/10 to-transparent p-5 sm:p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center"><Zap className="w-5 h-5 text-red-400" /></div>
                <div><h3 className="font-bold text-white">Exit Velocity</h3><p className="text-xs text-white/40">Hardest verified hits</p></div>
              </div>
              <div className="space-y-2">
                {topMetrics.exit_velocity?.slice(0, 5).map((p, i) => (
                  <Link to={`/athlete/${p.athlete_id}`} key={p.athlete_id} className="flex items-center justify-between p-2.5 sm:p-3 rounded-lg bg-white/5 hover:bg-white/10 transition group" data-testid={`ev-player-${i}`}>
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${i === 0 ? 'bg-red-500 text-white' : i === 1 ? 'bg-gray-400 text-black' : i === 2 ? 'bg-amber-700 text-white' : 'bg-white/10 text-white/60'}`}>{i+1}</span>
                      <div className="min-w-0"><span className="text-sm font-medium text-white group-hover:text-red-400 transition truncate block">{p.name}</span><span className="text-xs text-white/40 truncate block">{p.school}</span></div>
                    </div>
                    <span className="text-lg font-black text-red-400 flex-shrink-0 ml-2">{p.metrics?.exit_velocity} MPH</span>
                  </Link>
                ))}
                {topMetrics.exit_velocity?.length === 0 && <div className="text-center py-6 text-white/30 text-sm">No verified data yet</div>}
              </div>
            </div>
          </div>
          <div className="text-center mt-8">
            <Link to="/leaderboard" data-testid="view-leaderboard-btn">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/5">See Full Leaderboard <ArrowRight className="w-4 h-4 ml-2" /></Button>
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="relative z-10 py-16 sm:py-24 border-t border-white/5" data-testid="testimonials-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <span className="text-xs tracking-[0.4em] text-amber-400/80 uppercase">Success Stories</span>
            <h2 className="text-2xl sm:text-4xl font-black mt-2 tracking-tight">What They're Saying</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-4 sm:gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-white/5 p-6 relative" data-testid={`testimonial-${i}`}>
                <Quote size={24} className="text-red-500/30 mb-3" />
                <p className="text-sm text-white/70 leading-relaxed mb-4 italic">"{t.quote}"</p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-red-600/20 flex items-center justify-center text-xs font-bold text-red-400">{t.name.charAt(0)}</div>
                  <div><p className="text-sm font-bold text-white">{t.name}</p><p className="text-xs text-white/40 flex items-center gap-1"><MapPin size={10} /> {t.location}</p></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative z-10 py-16 sm:py-24 border-t border-white/5" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <span className="text-xs tracking-[0.4em] text-blue-400/80 uppercase">Questions</span>
            <h2 className="text-2xl sm:text-4xl font-black mt-2 tracking-tight">Frequently Asked</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-white/5 overflow-hidden" data-testid={`faq-${i}`}>
                <button className="w-full flex items-center justify-between p-4 sm:p-5 text-left hover:bg-white/5 transition" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span className="text-sm sm:text-base font-medium text-white pr-4">{faq.q}</span>
                  <ChevronDown size={18} className={`text-white/40 flex-shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-4 sm:px-5 pb-4 sm:pb-5"><p className="text-sm text-white/60 leading-relaxed">{faq.a}</p></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SCOUT WIRE TICKER */}
      <section className="relative z-10 py-4 border-t border-b border-white/5 bg-gradient-to-r from-red-500/5 via-transparent to-red-500/5 overflow-hidden" data-testid="scout-wire">
        <div className="flex items-center gap-4 animate-ticker whitespace-nowrap" ref={tickerRef}>
          <span className="px-3 py-1 bg-red-600 text-white text-xs font-bold rounded flex-shrink-0 tracking-wider">SCOUT WIRE</span>
          {[...scoutWireItems, ...scoutWireItems].map((item, i) => (
            <span key={i} className="text-sm text-white/50 flex-shrink-0 mx-6">{item}<span className="mx-6 text-red-500/40">|</span></span>
          ))}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="relative z-10 py-12 sm:py-16 border-t border-white/5" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-8 mb-10">
            <div>
              <img src="/nsb-footer-logo.jpeg" alt="National Scouting Bureau" className="h-16 sm:h-20 object-contain mb-4" data-testid="footer-logo" />
              <p className="text-sm text-white/40 leading-relaxed">Professional baseball scouting, evaluation, and recruiting by former College Coaches & MLB scouts.</p>
            </div>
            <div>
              <h4 className="text-sm font-bold text-white mb-3 uppercase tracking-wider">Quick Links</h4>
              <div className="space-y-2">
                {[
                  { label: "Free Signup", href: `${EXT_BASE}/free` },
                  { label: "Memberships", href: `${EXT_BASE}/membership` },
                  { label: "Evaluations", href: `${EXT_BASE}/combine` },
                  { label: "GameDay+", href: `${EXT_BASE}/gameday-plus` },
                  { label: "Combines", href: `${EXT_BASE}/combines` },
                ].map(link => (
                  <a key={link.label} href={link.href} target="_blank" rel="noopener noreferrer" className="block text-sm text-white/40 hover:text-white transition">{link.label}</a>
                ))}
                <Link to="/leaderboard" className="block text-sm text-white/40 hover:text-white transition">Leaderboard</Link>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-bold text-white mb-3 uppercase tracking-wider">Platform</h4>
              <div className="space-y-2">
                <Link to="/login?mode=player" className="block text-sm text-white/40 hover:text-white transition">Player Login</Link>
                <Link to="/login" className="block text-sm text-white/40 hover:text-white transition">Coach Login</Link>
                <Link to="/login?mode=admin" className="block text-sm text-white/40 hover:text-white transition">Admin Login</Link>
                <a href="https://scoutseyeapp.com" target="_blank" rel="noopener noreferrer" className="block text-sm text-white/40 hover:text-white transition flex items-center gap-1">
                  <Smartphone size={12} /> ScoutsEye App
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-bold text-white mb-3 uppercase tracking-wider">Legal</h4>
              <div className="space-y-2">
                {[
                  { label: "Privacy", href: `${EXT_BASE}/privacy` },
                  { label: "Terms & Conditions", href: `${EXT_BASE}/terms` },
                  { label: "Disclaimer", href: `${EXT_BASE}/disclaimer` },
                  { label: "Refund Policy", href: `${EXT_BASE}/refund` },
                  { label: "Service & Delivery", href: `${EXT_BASE}/service-delivery` },
                  { label: "Data Deletion Policy", href: `${EXT_BASE}/data-deletion` },
                ].map(link => (
                  <a key={link.label} href={link.href} target="_blank" rel="noopener noreferrer" className="block text-sm text-white/40 hover:text-white transition" data-testid={`footer-${link.label.toLowerCase().replace(/\s|&/g, '-')}`}>{link.label}</a>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-bold text-white mb-3 uppercase tracking-wider">Contact</h4>
              <div className="space-y-2">
                <a href="mailto:rod@scoutseyeapp.com" className="flex items-center gap-2 text-sm text-white/40 hover:text-white transition"><Mail size={14} /> rod@scoutseyeapp.com</a>
                <a href="tel:3345610275" className="flex items-center gap-2 text-sm text-white/40 hover:text-white transition"><Phone size={14} /> 334.561.0275</a>
              </div>
              <div className="flex gap-3 mt-4">
                <a href="https://twitter.com/NSBscouting" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/15 transition" data-testid="footer-social-x">
                  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white/60"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                </a>
                <a href="https://instagram.com/NSBscouting" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/15 transition" data-testid="footer-social-ig">
                  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white/60"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                </a>
                <a href="https://facebook.com/NSBscouting" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/15 transition" data-testid="footer-social-fb">
                  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white/60"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-white/5 pt-6">
            {/* ========== PATENT PENDING / IP PROTECTION BAR ========== */}
            <div className="mb-6 rounded-xl border border-amber-500/20 bg-gradient-to-r from-amber-500/[0.04] via-white/[0.02] to-red-600/[0.04] p-5 sm:p-6" data-testid="patent-pending-block">
              <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                <div className="flex items-center gap-3 sm:flex-col sm:items-center sm:gap-2 flex-shrink-0">
                  <div className="w-11 h-11 rounded-full border border-amber-500/40 bg-amber-500/10 flex items-center justify-center">
                    <ShieldCheck className="w-5 h-5 text-amber-400" />
                  </div>
                  <div className="text-center">
                    <div className="text-[10px] tracking-[0.25em] text-amber-300 uppercase font-bold leading-tight">Patent</div>
                    <div className="text-[10px] tracking-[0.25em] text-amber-300 uppercase font-bold leading-tight">Pending</div>
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-white/90 mb-2 flex items-center gap-2 flex-wrap">
                    Intellectual Property Protected
                    <span className="text-[9px] tracking-[0.2em] text-white/40 uppercase border border-white/15 rounded px-1.5 py-0.5">USPTO Filed</span>
                  </h4>
                  <p className="text-xs text-white/55 leading-relaxed">
                    The <span className="text-white/80 font-semibold">NSB Scholar-Athlete Operating System&trade;</span>, including the <span className="text-white/80 font-semibold">12-Month Recruiting Roadmap&trade;</span>, <span className="text-white/80 font-semibold">NSB Fusion&trade; Tuition Rewards architecture</span>, <span className="text-white/80 font-semibold">NSB Verified&trade; progressive authentication badge</span>, and <span className="text-white/80 font-semibold">Player Value Index&trade; (PVI)</span> are patent-pending proprietary methodologies of National Scouting Bureau, LLC. All scoring algorithms, data integration frameworks, recruiting workflows, and partnership architectures are protected trade secrets.
                  </p>
                  <p className="text-xs text-white/45 leading-relaxed mt-2">
                    Unauthorized replication, reverse engineering, derivative product development, white-labeling, or commercial reproduction is strictly prohibited and will be pursued to the fullest extent of U.S. and international intellectual property law. NSB Verified&trade;, NSB Fusion&trade;, Scholar-Athlete Operating System&trade;, and the NSB shield mark are trademarks of National Scouting Bureau, LLC. SAGE Scholars Tuition Rewards&reg; is a registered service mark of SAGE Scholars, Inc., used under exclusive integration partnership.
                  </p>
                  <p className="text-xs text-amber-300/80 mt-3">
                    Licensing, white-label, or strategic partnership inquiries: <a href="mailto:partnerships@nationalscoutingbureau.com" className="underline hover:text-amber-200" data-testid="patent-partnership-email">partnerships@nationalscoutingbureau.com</a>
                  </p>
                </div>
              </div>
            </div>

            <div className="text-center">
              <p className="text-xs text-white/30">Trusted by 450+ college programs nationwide. NSB-verified metrics are the gold standard in baseball recruiting.</p>
              <p className="text-xs text-white/15 mt-2">&copy; 2026 National Scouting Bureau, LLC. All rights reserved. Patent Pending. NSB Verified&trade;, NSB Fusion&trade;, and Scholar-Athlete Operating System&trade; are trademarks of National Scouting Bureau, LLC.</p>
            </div>
          </div>
        </div>
      </footer>

      {/* MOBILE STICKY CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-50 sm:hidden bg-black/95 backdrop-blur-xl border-t border-white/10 p-3" data-testid="mobile-sticky-cta">
        <a href={`${EXT_BASE}/free`} target="_blank" rel="noopener noreferrer" className="block">
          <Button className="w-full bg-gradient-to-r from-red-600 to-red-700 text-white py-3 text-sm font-bold shadow-[0_0_20px_rgba(220,38,38,0.3)]">
            <UserPlus size={16} className="mr-1" /> Join NSB Connect — Free
          </Button>
        </a>
      </div>

      <style>{`
        .stars-layer {
          position: absolute; inset: 0;
          background-image: radial-gradient(2px 2px at 20px 30px, rgba(255,255,255,0.15), transparent),
            radial-gradient(2px 2px at 40px 70px, rgba(255,255,255,0.1), transparent),
            radial-gradient(1px 1px at 90px 40px, rgba(255,255,255,0.12), transparent),
            radial-gradient(1px 1px at 130px 80px, rgba(255,255,255,0.08), transparent),
            radial-gradient(2px 2px at 160px 30px, rgba(239,68,68,0.15), transparent),
            radial-gradient(1px 1px at 200px 60px, rgba(255,255,255,0.1), transparent);
          background-size: 200px 100px;
          animation: twinkle 8s ease-in-out infinite alternate;
        }
        @keyframes twinkle { 0% { opacity: 0.6; } 100% { opacity: 1; } }
        @keyframes ticker { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .animate-ticker { animation: ticker 40s linear infinite; }
        .news-scroll::-webkit-scrollbar { width: 4px; }
        .news-scroll::-webkit-scrollbar-track { background: transparent; }
        .news-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 4px; }
        .news-scroll::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.2); }
      `}</style>
    </div>
  );
}
